/** @jsxImportSource react */
import { Box, Paper, Typography } from '@mui/material'
import { useDraggable } from '@dnd-kit/core'
import { FIELD_TYPES } from './builderModel'
import FieldTypeIcon from './FieldTypeIcon'

export const PALETTE_PREFIX = 'palette:'

const PaletteItem = ({ type, label, hint, disabled, onAdd }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `${PALETTE_PREFIX}${type}`, disabled })
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="button"
      aria-label={`Add ${label} field`}
      onClick={() => !disabled && onAdd(type)}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onAdd(type) } }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderRadius: 1,
        border: 1, borderColor: 'divider', bgcolor: 'background.paper',
        cursor: disabled ? 'default' : 'grab', opacity: disabled ? 0.5 : isDragging ? 0.4 : 1,
        userSelect: 'none', touchAction: 'none',
        '&:hover': disabled ? undefined : { borderColor: 'primary.main', color: 'primary.main' },
      }}
    >
      <FieldTypeIcon type={type} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>{label}</Typography>
        {hint && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{hint}</Typography>}
      </Box>
    </Box>
  )
}

/** Drag a type onto the canvas, or click it to append to the selected (or last) section. */
const FieldPalette = ({ disabled, onAdd }) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Fields</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
      Drag onto the form, or click to add
    </Typography>
    <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr 1fr', lg: '1fr' } }}>
      {FIELD_TYPES.map((t) => <PaletteItem key={t.type} {...t} disabled={disabled} onAdd={onAdd} />)}
    </Box>
  </Paper>
)

export default FieldPalette
